
import React from "react";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Loader2, BellRing, BellOff } from "lucide-react";
import { WaitlistFormValues } from "./schema";
import { PersonalDetailsSection } from "./form-sections/PersonalDetailsSection";
import { LocationPreferencesSection } from "./form-sections/LocationPreferencesSection";
import { CareRequirementsSection } from "./form-sections/CareRequirementsSection";
import { ResidentDetailsSection } from "./form-sections/ResidentDetailsSection";
import { TimelineSection } from "./form-sections/TimelineSection";
import { FinancialSection } from "./form-sections/FinancialSection";
import { AdditionalInformationSection } from "./form-sections/AdditionalInformationSection";
import { PrivacyConsentSection } from "./form-sections/PrivacyConsentSection";
import { useWaitlistForm } from "@/hooks/use-waitlist-form";
import { useWaitlistSubmission } from "@/hooks/use-waitlist-submission";
import { useWaitlistPreferences } from "@/hooks/use-waitlist-preferences";
import { Switch } from "@/components/ui/switch";

interface WaitlistFormProps {
  nursingHomeName?: string;
  countyName?: string;
  nursingHomeSlug?: string;
}

export function WaitlistForm({
  nursingHomeName,
  countyName,
  nursingHomeSlug
}: WaitlistFormProps) {
  const {
    form,
    user
  } = useWaitlistForm({
    countyName,
    nursingHomeName
  });

  const {
    isSubmitting,
    submitWaitlistForm,
    handleUnauthenticatedSubmission
  } = useWaitlistSubmission({
    nursingHomeName,
    countyName,
    nursingHomeSlug
  });

  const {
    waitlistActive,
    updateWaitlistStatus,
    savePreferences
  } = useWaitlistPreferences();

  async function onSubmit(data: WaitlistFormValues) {
    if (!user) {
      handleUnauthenticatedSubmission(data);
      return;
    }

    // Save preferences before submission
    await savePreferences(data);
    
    const success = await submitWaitlistForm(data, user.id);
    if (success) {
      form.reset();
    }
  }

  const handleWaitlistToggle = (checked: boolean) => {
    updateWaitlistStatus(checked);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        {user && (
          <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2">
              {waitlistActive ? (
                <BellRing className="h-5 w-5 text-grandpal-green" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-400" />
              )}
              <span className="font-medium">
                Waitlist {waitlistActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <Switch
              checked={waitlistActive}
              onCheckedChange={handleWaitlistToggle}
            />
          </div>
        )}

        <PersonalDetailsSection control={form.control} />
        <LocationPreferencesSection 
          control={form.control} 
          preselectedNursingHomeSlug={nursingHomeSlug}
          preselectedCounty={countyName ? countyName.toLowerCase().replace(/\s+/g, '-') : undefined}
        />
        <CareRequirementsSection control={form.control} />
        <ResidentDetailsSection control={form.control} />
        <TimelineSection control={form.control} />
        <FinancialSection control={form.control} />
        <AdditionalInformationSection control={form.control} />
        <PrivacyConsentSection control={form.control} />

        <Button type="submit" disabled={isSubmitting} className="w-full bg-grandpal-green hover:bg-grandpal-green-dark/90">
          {isSubmitting ? <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </> : user ? "Submit Waitlist Registration" : "Continue to Create Account"}
        </Button>
      </form>
    </Form>
  );
}
